import React, { memo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Avatar,
  Chip,
  IconButton,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import InlineAlert from '../../../shared/components/InlineAlert';
import { TabPanel } from '../../../shared/components/TabPanel';
import { useUserProfile, VIEW_AS_OPTIONS } from '../hooks/useUserProfile';

interface UserProfileModalProps {
  open: boolean;
  onClose: () => void;
}

const UserProfileModal: React.FC<UserProfileModalProps> = ({ open, onClose }) => {
  const {
    user, viewAsRole, setViewAsRole, logout, activeTab, setActiveTab, tabs, sessionInfo,
    assignedLevelLabel, viewAsLabel,
  } = useUserProfile(open);

  if (!user) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        User Profile
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
        >
          {tabs.map(t => (
            <Tab key={t.key} label={t.label} value={t.key} />
          ))}
        </Tabs>
      </Box>

      <DialogContent sx={{ p: 0, minHeight: 400 }}>
        {/* Profile Tab */}
        <TabPanel index="profile" value={activeTab}>
          <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Avatar sx={{ width: 56, height: 56, bgcolor: viewAsRole ? 'warning.main' : 'primary.main', fontSize: '1.5rem' }}>
                {(user.name || user.email).charAt(0).toUpperCase()}
              </Avatar>
              <Box>
                <Typography variant="h6">{user.name || user.email}</Typography>
                <Typography variant="body2" color="text.secondary">{user.email}</Typography>
              </Box>
            </Box>

            {viewAsRole && (
              <InlineAlert severity="warning" sx={{ mb: 3 }}>
                You are viewing as <strong>{viewAsLabel}</strong>.
                Your identity is preserved; the level is simulated and your admin roles are set aside.
              </InlineAlert>
            )}

            {/* Identity */}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Identity</Typography>
            <Box sx={{ mb: 3, pl: 1 }}>
              <Typography variant="body2" color="text.secondary">
                User ID: <Typography component="span" variant="body2" sx={{ fontFamily: 'monospace' }}>{user.sub}</Typography>
              </Typography>
            </Box>

            {/* Groups */}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Groups</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3, pl: 1 }}>
              {user.groups && user.groups.length > 0 ? (
                user.groups.map(g => <Chip key={g} label={g} size="small" variant="outlined" />)
              ) : (
                <Typography variant="body2" color="text.secondary">None</Typography>
              )}
            </Box>

            {/* Level and roles */}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Assigned Level</Typography>
            <Typography variant="body2" data-testid="assigned-level" sx={{ mb: 3, pl: 1 }}>
              {assignedLevelLabel}
            </Typography>

            <Typography variant="subtitle2" sx={{ mb: 1 }}>Roles</Typography>
            <Box data-testid="roles" sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', pl: 1 }}>
              {user.roles.length > 0 ? (
                user.roles.map(r => <Chip key={r} label={r} size="small" color="primary" variant="outlined" />)
              ) : (
                <Typography variant="body2" color="text.secondary">None</Typography>
              )}
            </Box>
          </Box>
        </TabPanel>

        {/* View As Tab (admin only) */}
        <TabPanel index="viewas" value={activeTab}>
          <Box sx={{ p: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Preview a level to see how OpsPilot behaves for an operator holding it.
              Your identity stays the same; your level is replaced and your admin roles are set aside.
            </Typography>

            {viewAsRole && (
              <InlineAlert
                severity="warning"
                sx={{ mb: 2 }}
                action={
                  <Button
                    color="warning"
                    size="small"
                    startIcon={<VisibilityOffIcon />}
                    onClick={() => setViewAsRole(null)}
                  >
                    Reset
                  </Button>
                }
              >
                Currently viewing as: <strong>{viewAsLabel}</strong>
              </InlineAlert>
            )}

            <Typography variant="subtitle2" sx={{ mb: 1 }}>Level</Typography>
            <List disablePadding>
              {VIEW_AS_OPTIONS.map(({ role, label, description }) => (
                <ListItem key={role} disablePadding>
                  <ListItemButton
                    selected={viewAsRole === role}
                    onClick={() => setViewAsRole(viewAsRole === role ? null : role)}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{
                        width: 36,
                        height: 36,
                        bgcolor: viewAsRole === role ? 'warning.main' : 'action.hover',
                      }}>
                        <VisibilityIcon fontSize="small" color={viewAsRole === role ? 'inherit' : 'action'} />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText primary={label} secondary={description} />
                    {viewAsRole === role && (
                      <Chip label="Active" color="warning" size="small" />
                    )}
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>
        </TabPanel>

        {/* Session Tab */}
        <TabPanel index="session" value={activeTab}>
          <Box sx={{ p: 3 }}>
            {sessionInfo ? (
              <>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Session Details</Typography>
                  <Box sx={{ pl: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Login Time</Typography>
                      <Typography variant="body2">{sessionInfo.loginTime}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Token Expires</Typography>
                      <Typography variant="body2">{sessionInfo.expiresAt}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Time Remaining</Typography>
                      <Typography variant="body2" color={sessionInfo.timeRemaining === 'Expired' ? 'error.main' : 'text.primary'}>
                        {sessionInfo.timeRemaining}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<LogoutIcon />}
                  onClick={logout}
                  fullWidth
                >
                  Sign Out
                </Button>
              </>
            ) : (
              <Typography color="text.secondary">No session information available.</Typography>
            )}
          </Box>
        </TabPanel>
      </DialogContent>
    </Dialog>
  );
};

export default memo(UserProfileModal);
